import { Session } from 'data/firestore/session/types';
import { EntityGet } from 'data/firestore/types';
import { DateTime } from 'luxon';
import { Hour } from '../types';

type CallDirection = 'inbound' | 'outbound';

type AggregatedCalls = {
    totalInboundCalls: number;
    totalInboundSeconds: number;
    totalOutboundCalls: number;
    totalOutboundSeconds: number;
    inboundCallsByHour: Record<Hour, number>;
    outboundCallsByHour: Record<Hour, number>;
    inboundCallsByDate: Record<string, number>;
    outboundCallsByDate: Record<string, number>;
    inboundDurationByDate: Record<string, number>;
    outboundDurationByDate: Record<string, number>;
};

// Create an object with all hours initialized to 0
const emptyCallsByHour = (): Record<Hour, number> => Array.from({ length: 24 }, (_, i) => i)
    .reduce((acc, hour) => ({ ...acc, [hour]: 0 }), {} as Record<Hour, number>);

// Initialize a record with the last 30 days set to 0, starting with the most recent day
const initializeLastThirtyDays = (): Record<string, number> => {
    const today = DateTime.now();

    return Array.from({ length: 30 }, (_, i) => {
        const date = today.minus({ days: 29 - i });
        return date.toFormat('MMM d');
    }).reduce((acc, date) => ({ ...acc, [date]: 0 }), {});
};

// Extract date and hour from a call
const getCallTimeInfo = (call: EntityGet<Session>) => ({
    hour: new Date(call.createdAt).getHours() as Hour,
    date: DateTime.fromJSDate(call.createdAt).toFormat('MMM d'),
    duration: call.duration || 0,
});

// Update accumulator for a specific direction
const updateDirectionStats = (
    acc: AggregatedCalls,
    direction: CallDirection,
    {
        hour,
        date,
        duration,
    }: {
        hour: Hour;
        date: string;
        duration: number
    },
): AggregatedCalls => {
    const isInbound = direction === 'inbound';
    const dirPrefix = isInbound ? 'inbound' : 'outbound';
    const totalKey = `total${isInbound ? 'Inbound' : 'Outbound'}Calls` as const;
    const secondsKey = `total${isInbound ? 'Inbound' : 'Outbound'}Seconds` as const;

    return {
        ...acc,
        [totalKey]: acc[totalKey] + 1,
        [secondsKey]: acc[secondsKey] + duration,
        [`${dirPrefix}CallsByHour`]: {
            ...acc[`${dirPrefix}CallsByHour`],
            [hour]: acc[`${dirPrefix}CallsByHour`][hour] + 1,
        },
        [`${dirPrefix}CallsByDate`]: {
            ...acc[`${dirPrefix}CallsByDate`],
            [date]: (acc[`${dirPrefix}CallsByDate`][date] || 0) + 1,
        },
        [`${dirPrefix}DurationByDate`]: {
            ...acc[`${dirPrefix}DurationByDate`],
            [date]: (acc[`${dirPrefix}DurationByDate`][date] || 0) + duration,
        },
    };
};

// Find peak hour from a calls-by-hour record
const findPeakHour = (callsByHour: Record<Hour, number>): Hour => Object.entries(callsByHour)
    .reduce((peak, [hour, count]) => {
        const hourNum = Number(hour) as Hour;
        return Number(count) > callsByHour[peak] ? hourNum : peak;
    }, 0 as Hour);

// Calculate averages with safe division
const safeAverage = (
    numerator: number,
    denominator: number,
    factor = 1,
): number => (denominator ? numerator / denominator / factor : 0);

const getSummary = (calls: EntityGet<Session>[]) => {
    // Initialize accumulator
    const initialAcc: AggregatedCalls = {
        totalInboundCalls: 0,
        totalInboundSeconds: 0,
        totalOutboundCalls: 0,
        totalOutboundSeconds: 0,
        inboundCallsByHour: emptyCallsByHour(),
        outboundCallsByHour: emptyCallsByHour(),
        inboundCallsByDate: initializeLastThirtyDays(),
        outboundCallsByDate: initializeLastThirtyDays(),
        inboundDurationByDate: initializeLastThirtyDays(),
        outboundDurationByDate: initializeLastThirtyDays(),
    };

    // Aggregate call data
    const aggregated = calls.reduce<AggregatedCalls>((acc, call) => {
        const timeInfo = getCallTimeInfo(call);
        const direction = call.direction as CallDirection;

        return updateDirectionStats(acc, direction, timeInfo);
    }, initialAcc);

    const {
        totalInboundCalls,
        totalOutboundCalls,
        totalInboundSeconds,
        totalOutboundSeconds,
        inboundCallsByHour,
        outboundCallsByHour,
    } = aggregated;

    const DAYS_IN_PERIOD = 30;

    return {
        totalCalls: calls.length,
        ...aggregated,

        // Calculate averages
        averageInboundCallsPerDay: safeAverage(totalInboundCalls, DAYS_IN_PERIOD),
        averageOutboundCallsPerDay: safeAverage(totalOutboundCalls, DAYS_IN_PERIOD),
        averageInboundCallDuration: safeAverage(totalInboundSeconds, totalInboundCalls),
        averageOutboundCallDuration: safeAverage(totalOutboundSeconds, totalOutboundCalls),
        averageInboundCallDurationPerDay:
            safeAverage(totalInboundSeconds, totalInboundCalls, DAYS_IN_PERIOD),
        averageOutboundCallDurationPerDay:
            safeAverage(totalOutboundSeconds, totalOutboundCalls, DAYS_IN_PERIOD),

        // Find peak hours
        inboundPeakHour: totalInboundCalls ? findPeakHour(inboundCallsByHour) : 0,
        outboundPeakHour: totalOutboundCalls ? findPeakHour(outboundCallsByHour) : 0,
    };
};

export default getSummary;
